if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          evt.detail.variant.price
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {?number} variantPrice - current variant price in cents
     */
    updateInventory(count, available, inventoryPolicy, variantPrice = null) {
      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }

      this.updatePurchaseSummaryAvailability(inventoryLevel, variantPrice);
    }

    updatePurchaseSummaryAvailability(inventoryLevel, variantPrice = null) {
      const productInfo = this.closest('.product-info');
      const availabilityLines = productInfo?.querySelectorAll('.js-purchase-availability-line');
      if (!availabilityLines?.length) return;

      const labels = {
        very_low: 'Very low stock',
        low: 'Low stock',
        normal: 'In stock (order now for earlier dispatch)',
        in_stock: 'In stock (order now for earlier dispatch)',
        backordered: 'Available to order',
        none: 'Currently unavailable'
      };

      const label = labels[inventoryLevel] || 'In stock (order now for earlier dispatch)';
      const showShipping = inventoryLevel !== 'none' && inventoryLevel !== 'backordered';

      availabilityLines.forEach((availabilityLine) => {
        const summary = availabilityLine.closest('.product-purchase-summary') || productInfo;
        const shippingNote = summary?.querySelector('.js-purchase-shipping-note');
        let deliveryEstimate = summary?.querySelector('.js-purchase-delivery-estimate');
        deliveryEstimate = this.ensurePurchaseDeliveryEstimate(summary, shippingNote, deliveryEstimate);

        const threshold = Number.parseInt(availabilityLine.dataset.freeShippingThreshold || '', 10);
        const price = Number.parseInt(variantPrice, 10);
        const hasPriceThreshold = Number.isFinite(threshold) && Number.isFinite(price);
        const qualifiesForFreeShipping = hasPriceThreshold && price >= threshold;
        const shippingLabel = hasPriceThreshold
          ? (qualifiesForFreeShipping ? availabilityLine.dataset.freeShippingLabel : availabilityLine.dataset.paidShippingLabel)
          : availabilityLine.dataset.shippingLabel;
        const availableNote = hasPriceThreshold
          ? (qualifiesForFreeShipping ? shippingNote?.dataset.freeNote : shippingNote?.dataset.paidNote)
          : shippingNote?.dataset.availableNote;
        if (deliveryEstimate) {
          this.applyPurchaseDeliveryEstimateSettings(deliveryEstimate, this.getPurchaseDeliverySettings(summary));
        }
        const estimateMode = hasPriceThreshold ? (qualifiesForFreeShipping ? 'free' : 'paid') : 'available';
        const availableEstimate = hasPriceThreshold
          ? (this.formatPurchaseDeliveryEstimate(deliveryEstimate, estimateMode)
            || (qualifiesForFreeShipping ? deliveryEstimate?.dataset.freeEstimate : deliveryEstimate?.dataset.paidEstimate))
          : deliveryEstimate?.dataset.availableEstimate;

        availabilityLine.hidden = false;
        availabilityLine.innerText = label;
        if (shippingNote) {
          shippingNote.innerText = showShipping ? availableNote || '' : '';
        }
        if (deliveryEstimate) {
          deliveryEstimate.innerHTML = showShipping ? (this.formatPurchaseDeliveryEstimate(deliveryEstimate, estimateMode) || availableEstimate || '') : '';
        }
      });
    }

    getPurchaseDeliverySettings(context = document) {
      const destination = context?.querySelector?.('.js-purchase-delivery-destination');
      const country = (
        destination?.dataset.country
        || window.Shopify?.country
        || 'US'
      ).toUpperCase();
      const europeCountryCodes = new Set([
        'AL', 'AD', 'AT', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI',
        'FR', 'DE', 'GI', 'GR', 'GG', 'HU', 'IS', 'IE', 'IM', 'IT', 'JE', 'LV',
        'LI', 'LT', 'LU', 'MT', 'MC', 'ME', 'NL', 'MK', 'NO', 'PL', 'PT', 'RO',
        'SM', 'RS', 'SK', 'SI', 'ES', 'SE', 'CH', 'TR', 'GB', 'VA'
      ]);
      const asiaPacificCountryCodes = new Set(['AU', 'NZ', 'JP', 'SG', 'HK', 'KR', 'MY', 'PH', 'TH', 'VN', 'ID', 'TW', 'IN']);

      let shippingLabelFree = 'FREE delivery';
      let shippingLabelPaid = 'Delivery';
      let deliveryNoteFree = '';
      let deliveryNotePaid = '';
      let estimatePrefix = 'FREE delivery';
      let estimateMinDays = 4;
      let estimateMaxDays = 4;
      let estimateSuffix = '';
      let estimateSingleDate = true;
      let fastestPrefix = 'or fastest delivery';
      let fastestDays = 3;

      if (country !== 'US') {
        shippingLabelFree = 'international shipping at checkout';
        shippingLabelPaid = 'international shipping at checkout';
        deliveryNoteFree = '';
        deliveryNotePaid = '';
        estimatePrefix = 'Delivery on';
        estimateMinDays = 10;
        estimateMaxDays = 25;
        estimateSuffix = '';
        estimateSingleDate = true;
        fastestPrefix = 'or fastest delivery';
        fastestDays = 10;

        if (country === 'CA') {
          estimatePrefix = 'Delivery on';
          estimateMinDays = 7;
          estimateMaxDays = 10;
          fastestDays = 7;
        } else if (europeCountryCodes.has(country)) {
          estimatePrefix = 'Delivery on';
          estimateMinDays = 7;
          estimateMaxDays = 15;
          fastestDays = 7;
        } else if (asiaPacificCountryCodes.has(country)) {
          estimatePrefix = 'Delivery on';
          estimateMinDays = 12;
          estimateMaxDays = 20;
          fastestDays = 12;
        }
      }

      return {
        shippingLabelFree,
        shippingLabelPaid,
        deliveryNoteFree,
        deliveryNotePaid,
        estimatePrefix,
        estimateMinDays,
        estimateMaxDays,
        estimateSuffix,
        estimateSingleDate,
        fastestPrefix,
        fastestDays
      };
    }

    ensurePurchaseDeliveryEstimate(productInfo, shippingNote, deliveryEstimate) {
      if (deliveryEstimate || !productInfo || !shippingNote) return deliveryEstimate;

      const settings = this.getPurchaseDeliverySettings();
      const availabilityLine = productInfo.querySelector('.js-purchase-availability-line');
      if (availabilityLine) {
        availabilityLine.dataset.freeShippingThreshold = availabilityLine.dataset.freeShippingThreshold || '4900';
        availabilityLine.dataset.freeShippingLabel = availabilityLine.dataset.freeShippingLabel || settings.shippingLabelFree;
        availabilityLine.dataset.paidShippingLabel = availabilityLine.dataset.paidShippingLabel || settings.shippingLabelPaid;
        availabilityLine.dataset.shippingLabel = availabilityLine.dataset.shippingLabel || settings.shippingLabelPaid;
      }

      shippingNote.dataset.freeNote = shippingNote.dataset.freeNote || settings.deliveryNoteFree;
      shippingNote.dataset.paidNote = shippingNote.dataset.paidNote || settings.deliveryNotePaid;
      shippingNote.dataset.availableNote = shippingNote.dataset.availableNote || settings.deliveryNotePaid;

      const estimate = document.createElement('span');
      estimate.className = 'product-purchase-summary__estimate js-purchase-delivery-estimate';
      this.applyPurchaseDeliveryEstimateSettings(estimate, settings);
      shippingNote.insertAdjacentElement('afterend', estimate);

      return estimate;
    }

    applyPurchaseDeliveryEstimateSettings(deliveryEstimate, settings) {
      if (!deliveryEstimate || !settings) return;

      const estimateText = this.formatPurchaseDeliveryEstimateFromSettings(settings);
      ['free', 'paid', 'available'].forEach((mode) => {
        const prefixKey = `${mode}EstimatePrefix`;
        const minKey = `${mode}EstimateMinDays`;
        const maxKey = `${mode}EstimateMaxDays`;
        const suffixKey = `${mode}EstimateSuffix`;
        const singleDateKey = `${mode}EstimateSingleDate`;
        const fastestPrefixKey = `${mode}FastestPrefix`;
        const fastestDaysKey = `${mode}FastestDays`;
        const textKey = `${mode}Estimate`;

        deliveryEstimate.dataset[prefixKey] = deliveryEstimate.dataset[prefixKey] || settings.estimatePrefix;
        deliveryEstimate.dataset[minKey] = deliveryEstimate.dataset[minKey] || String(settings.estimateMinDays);
        deliveryEstimate.dataset[maxKey] = deliveryEstimate.dataset[maxKey] || String(settings.estimateMaxDays);
        deliveryEstimate.dataset[suffixKey] = deliveryEstimate.dataset[suffixKey] || settings.estimateSuffix;
        deliveryEstimate.dataset[singleDateKey] = deliveryEstimate.dataset[singleDateKey] || String(Boolean(settings.estimateSingleDate));
        deliveryEstimate.dataset[fastestPrefixKey] = deliveryEstimate.dataset[fastestPrefixKey] || settings.fastestPrefix || '';
        deliveryEstimate.dataset[fastestDaysKey] = deliveryEstimate.dataset[fastestDaysKey] || String(settings.fastestDays || 0);
        deliveryEstimate.dataset[textKey] = deliveryEstimate.dataset[textKey] || estimateText;
      });
    }

    formatPurchaseDeliveryEstimateFromSettings(settings) {
      return this.formatDeliveryEstimateText(
        settings.estimatePrefix,
        settings.estimateMinDays,
        settings.estimateMaxDays,
        settings.estimateSuffix,
        settings.estimateSingleDate,
        settings.fastestPrefix,
        settings.fastestDays
      );
    }

    formatPurchaseDeliveryEstimate(deliveryEstimate, mode = 'available') {
      if (!deliveryEstimate) return '';

      const prefix = deliveryEstimate.dataset[`${mode}EstimatePrefix`] || deliveryEstimate.dataset.availableEstimatePrefix;
      const minDays = Number.parseInt(deliveryEstimate.dataset[`${mode}EstimateMinDays`] || deliveryEstimate.dataset.availableEstimateMinDays || '', 10);
      const maxDays = Number.parseInt(deliveryEstimate.dataset[`${mode}EstimateMaxDays`] || deliveryEstimate.dataset.availableEstimateMaxDays || '', 10);
      const suffix = deliveryEstimate.dataset[`${mode}EstimateSuffix`] || deliveryEstimate.dataset.availableEstimateSuffix || '';
      const singleDate = (deliveryEstimate.dataset[`${mode}EstimateSingleDate`] || deliveryEstimate.dataset.availableEstimateSingleDate) === 'true';
      const fastestPrefix = deliveryEstimate.dataset[`${mode}FastestPrefix`] || deliveryEstimate.dataset.availableFastestPrefix || '';
      const fastestDays = Number.parseInt(deliveryEstimate.dataset[`${mode}FastestDays`] || deliveryEstimate.dataset.availableFastestDays || '', 10);

      if (!prefix || !Number.isFinite(minDays) || !Number.isFinite(maxDays)) return '';
      // free/paid modes are self-evident; 'available' carries the server's US free-shipping qualification via
      // data-available-free so the FREE cue survives the initial JS render (which runs in 'available' mode).
      const isFree = mode === 'free' || (mode === 'available' && deliveryEstimate.dataset.availableFree === 'true');
      return this.formatDeliveryEstimateText(prefix, minDays, maxDays, suffix, singleDate, fastestPrefix, fastestDays, isFree);
    }

    // eslint-disable-next-line no-unused-vars
    formatDeliveryEstimateText(prefix, minDays, maxDays, suffix = '', singleDate = false, fastestPrefix = '', fastestDays = Number.NaN, isFree = false) {
      const dateRange = this.formatDeliveryDateRange(minDays, maxDays, singleDate);
      const suffixText = suffix ? ` ${this.escapeHtml(suffix)}` : '';
      const detailsLink = '<a class="product-purchase-summary__details" href="/policies/shipping-policy">Details</a>';
      // Consolidated single line: "[FREE ]Delivery on <bold date>[ suffix] · or faster Details". FREE is a styled
      // span (not part of the prefix) so it renders identically to the Liquid server output. "or faster" is a static
      // upgrade affordance pointing at the single Details link (no second date) when a faster option exists.
      const freeText = isFree ? '<strong class="product-purchase-summary__free">FREE</strong> ' : '';
      const fasterText = Number.isFinite(fastestDays) && fastestDays > 0 ? ' &middot; or faster ' : ' ';
      return `${freeText}${this.escapeHtml(prefix)} <strong class="product-purchase-summary__date">${this.escapeHtml(dateRange)}</strong>${suffixText}${fasterText}${detailsLink}`;
    }

    escapeHtml(value) {
      const replacements = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      };
      return String(value ?? '').replace(/[&<>"']/g, (char) => replacements[char]);
    }

    formatDeliveryDateRange(minDays, maxDays, singleDate = false) {
      return window.LPWDeliveryDates?.formatDeliveryDateRange?.(minDays, maxDays, { singleDate }) || '';
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
